import { Injectable, isDevMode } from '@angular/core';
import { StorageProvider } from './local-storage.interface';
import { UserSettings, DEFAULT_USER_SETTINGS } from '../../models/user-settings';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageProvider implements StorageProvider {
    private readonly STORAGE_KEY = 'user_settings';

    async getState(): Promise<UserSettings> {
        try {
            const storedState = localStorage.getItem(this.STORAGE_KEY);
            if (storedState) {
                const parsedState = JSON.parse(storedState);
                return this.validateAndMergeState(parsedState);
            }
            return { ...DEFAULT_USER_SETTINGS };
        } catch (error) {
            this.logError('Failed to load state from localStorage', error);
            return { ...DEFAULT_USER_SETTINGS };
        }
    }

    async setState(state: UserSettings): Promise<void> {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
        } catch (error) {
            this.logError('Failed to save state to localStorage', error);
            throw error;
        }
    }

    async clearState(): Promise<void> {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            this.logError('Failed to clear localStorage', error);
            throw error;
        }
    }

    private validateAndMergeState(storedState: any): UserSettings {
        const validatedState: UserSettings = { ...DEFAULT_USER_SETTINGS };

        if (storedState && typeof storedState === 'object' && storedState.mediaPlayer) {
            const mediaPlayer = storedState.mediaPlayer as { volume?: number | null; playbackSpeed?: number | null };
            validatedState.mediaPlayer = {
                volume: this.validateNumber(mediaPlayer.volume ?? null, DEFAULT_USER_SETTINGS.mediaPlayer.volume, 0, 1),
                playbackSpeed: this.validateNumber(mediaPlayer.playbackSpeed ?? null, DEFAULT_USER_SETTINGS.mediaPlayer.playbackSpeed, 0.5, 2),
            };
        }

        return validatedState;
    }

    private validateNumber(value: number | null | undefined, defaultValue: number, min: number, max: number): number {
        if (typeof value !== 'number' || isNaN(value) || value === null || value === undefined) {
            return defaultValue;
        }
        return Math.max(min, Math.min(max, value));
    }

    private logError(message: string, error: any): void {
        if (isDevMode()) {
            console.error(`[LocalStorageProvider] ${message}`, error);
        }
    }
}